'use client';

import { Link } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import { ArrowRight, Briefcase } from 'lucide-react';

interface AdvocateClaimPromptProps {
  headline: string;
  description: string;
  cta?: string;
  className?: string;
  iconClassName?: string;
  iconSize?: number;
  showCtaText?: boolean;
}

/**
 * Shared advocate claim prompt used by the sidebar banner and the anonymous
 * homepage CTA. Keeps the visual treatment consistent while letting each
 * surface supply its own copy and sizing. Always links to /advokati.
 */
export function AdvocateClaimPrompt({
  headline,
  description,
  cta,
  className,
  iconClassName,
  iconSize = 16,
  showCtaText = false,
}: AdvocateClaimPromptProps) {
  return (
    <Link
      href='/advokati'
      className={cn(
        'flex items-start gap-3 border border-brand-tertiary/40',
        'bg-gradient-to-br from-brand-tertiary/10 to-brand-tertiary/5',
        'transition-all hover:border-brand-tertiary/60 hover:bg-brand-tertiary/15',
        'group cursor-pointer',
        className
      )}
    >
      <div
        className={cn(
          'flex shrink-0 items-center justify-center rounded-md bg-brand-tertiary/20 text-brand-tertiary',
          iconClassName
        )}
      >
        <Briefcase size={iconSize} aria-hidden='true' />
      </div>

      <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <p className='text-xs font-semibold text-content-primary'>{headline}</p>
        <p className='text-xs text-content-secondary'>{description}</p>
        {showCtaText && cta && (
          <p className='mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand-tertiary transition-colors group-hover:text-brand-tertiary/80'>
            {cta}
            <ArrowRight
              size={14}
              aria-hidden='true'
              className='transition-transform group-hover:translate-x-0.5'
            />
          </p>
        )}
      </div>

      {!showCtaText && (
        <div className='flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-brand-tertiary/60 transition-all group-hover:text-brand-tertiary group-hover:bg-brand-tertiary/10 mt-0.5'>
          <ArrowRight size={14} aria-hidden='true' />
        </div>
      )}
    </Link>
  );
}
